import { SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAxios } from "../../utils/endpoints";
import { DropdownValue } from "../../types/DropdownValue";
import { RefundId, RefundValidate } from "../../types/Refund";
import { setISODay } from "date-fns";

interface IState {
    leadId: string;
    phone: string;
    reason: string;
    notes: string;
    zip: string;
}

const initialState: IState = {
    leadId: "",
    notes: "",
    phone: "",
    reason: "",
    zip: ""
}

export const useLeadRefundForm = () => {
    const [state, setState] = useState<IState>(initialState);

    const [inquiryDate, setInquiryDate] = useState<Date | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [reasons, setReasons] = useState<DropdownValue[]>([]);
    const [message, setMessage] = useState<string>("");
    const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
    const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
    
    const openSnackbar = () => setIsSnackbarOpen(true);
    const closeSnackbar = () => setIsSnackbarOpen(false);

    const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { name, value } = event.target

        setState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    }

    const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
        const { name, value } = event.target;
    
        setState((prevState) => ({
            ...prevState,
            [name]: value as string
        }));
    }

    const onOpenFileExplorer = (): void => {
        const fileBtn: HTMLInputElement | null = document.getElementById("file") as HTMLInputElement;

        if (fileBtn) {
            fileBtn.click();
        }
    }

    const onChangeDate = (newDate: Date): void => {
        setInquiryDate(newDate);
    }

    const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            setFile(event.target.files[0]);
        } else setFile(null);
    }

    const onRemoveFile = (): void => setFile(null);

    const onFetchReasons = async () => {
        const response = await getAxios("refund/reasons", "GET");

        const data: DropdownValue[] = await response.data;

        setReasons(data);
    }

    const isFieldEmpty = (): boolean => {
        if (file === null) {
            setSnackbarType("error");
            setMessage("File is mandatory");
            openSnackbar();

            return true;
        }

        for (const key in state) {
            const objKey = key as keyof typeof state;
        
            if (state[objKey] === "") {
                setSnackbarType("error");
                setMessage("All fields are mandatory");
                openSnackbar();

                return true;
            }
        }

        return false;
    }

    const onValidate = async (): Promise<RefundValidate> => {
        const response = await getAxios("/refund/validate", "GET", undefined, {
            leadId: state.leadId,
            inquiryDate: inquiryDate,
            phone: state.phone,
            reason: state.reason,
            zip: state.zip
        });

        return await response.data;
    }

    const onSubmit = async (): Promise<void> => {
        if (isFieldEmpty()) {
            return;
        }

        const validateResponse: RefundValidate = await onValidate();

        if (!validateResponse.doesExist) {
            setMessage(validateResponse.message);
            setSnackbarType("error");
            openSnackbar();

            return;
        }

        const response = await getAxios("/refund", "POST", {
            leadId: state.leadId,
            inquiryDate: inquiryDate,
            phone: state.phone,
            notes: state.notes,
            reasonId: state.reason,
            zip: state.zip,
            file: file
        }, undefined, "multipart/form-data");

        const refundData: RefundId = await response.data;

        setMessage(`Refund ${refundData.refundId} has been saved`);
        setSnackbarType("success");
        openSnackbar();

        setInquiryDate(null);
        setFile(null);
        setState(initialState);
    }

    useEffect(() => {
        onFetchReasons();
    }, [])

    return {
        state,
        inquiryDate,
        file,
        reasons,
        message,
        isSnackbarOpen,
        snackbarType,
        closeSnackbar,
        onSubmit,
        onChangeSelect,
        onChange,
        onChangeFile,
        onChangeDate,
        onRemoveFile,
        onOpenFileExplorer
    }
}